"use client"

import { useState, useEffect, useRef } from "react"
import {
  getGenerateFeedProgressLabel,
  getSimulatedGenerationProgress,
} from "@/shared/lib/simulated-generation-progress"

const TICK_MS = 100
const COMPLETION_MS = 400

/** Stagger so each image slot starts and finishes slightly after the previous (Veo-style). */
const SLOT_START_DELAY_MS = 400
const SLOT_COMPLETION_STAGGER_MS = 150

/**
 * Independent simulated progress per image slot while `isActive` is true.
 * Uses the same ~50s curve as chat; `backendFloor` lifts all slots when the stream reports progress.
 */
export function useMultiSlotProgress(
  isActive: boolean,
  count: number,
  backendFloor = 0,
) {
  const safeCount = Math.min(Math.max(count, 1), 8)
  const [progresses, setProgresses] = useState<number[]>(() => Array(safeCount).fill(0))
  const [phaseLabel, setPhaseLabel] = useState<string>("Getting ready...")
  const [isComplete, setIsComplete] = useState(false)

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const slotStartRef = useRef<number[]>([])
  const progressesRef = useRef<number[]>(Array(safeCount).fill(0))
  const completingRef = useRef(false)
  const completionBaseTimeRef = useRef(0)
  const progressAtCompletionRef = useRef<number[]>([])
  const wasActiveRef = useRef(false)
  const backendFloorRef = useRef(backendFloor)

  useEffect(() => {
    backendFloorRef.current = backendFloor
  }, [backendFloor])

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      const now = Date.now()
      completingRef.current = false
      slotStartRef.current = Array.from(
        { length: safeCount },
        (_, i) => now + i * SLOT_START_DELAY_MS
      )
      const initial = Array(safeCount).fill(0)
      progressesRef.current = initial
      setProgresses(initial)
      setPhaseLabel("Getting ready...")
      setIsComplete(false)

      intervalRef.current = setInterval(() => {
        const tickNow = Date.now()
        const floor = backendFloorRef.current

        if (completingRef.current) {
          const next = progressAtCompletionRef.current.map((startVal, i) => {
            const slotStart = completionBaseTimeRef.current + i * SLOT_COMPLETION_STAGGER_MS
            if (tickNow < slotStart) return startVal
            const elapsed = tickNow - slotStart
            const t = Math.min(elapsed / COMPLETION_MS, 1)
            return startVal + (100 - startVal) * t
          })
          progressesRef.current = next
          setProgresses(next)

          if (next.every((v) => v >= 99.99)) {
            const done = Array(safeCount).fill(100)
            progressesRef.current = done
            setProgresses(done)
            setPhaseLabel("Done!")
            setIsComplete(true)
            if (intervalRef.current) clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          return
        }

        const next = Array(safeCount).fill(0)
        let leadElapsed = 0
        let leadPercent = 0

        for (let i = 0; i < safeCount; i++) {
          if (tickNow < slotStartRef.current[i]) {
            next[i] = 0
            continue
          }

          const elapsedSec = (tickNow - slotStartRef.current[i]) / 1000
          const { percent } = getSimulatedGenerationProgress(elapsedSec, floor)
          next[i] = percent

          if (i === 0) {
            leadElapsed = elapsedSec
            leadPercent = percent
          }
        }

        progressesRef.current = next
        setProgresses(next)
        setPhaseLabel(getGenerateFeedProgressLabel(leadElapsed, leadPercent, floor))
      }, TICK_MS)
    }

    if (!isActive && wasActiveRef.current) {
      completingRef.current = true
      completionBaseTimeRef.current = Date.now()
      progressAtCompletionRef.current = [...progressesRef.current]
    }

    wasActiveRef.current = isActive

    return () => {
      if (!isActive && intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isActive, safeCount])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  return { progresses, phaseLabel, isComplete }
}
